import React, { useState, useEffect } from 'react';
import { Box, Switch, FormControlLabel, Typography, Tooltip, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Clock } from 'lucide-react';
import api from '../services/api';

// Switch estilo iPhone
const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#65C466',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color:
        theme.palette.mode === 'light'
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

/**
 * Componente de Switch para controle de horário fixado
 * 
 * Props:
 * - date: string no formato 'YYYY-MM-DD'
 * - dayOfWeek: string (MONDAY, TUESDAY, etc.)
 * - initialEnabled: boolean (estado inicial)
 * - onChange: callback(isEnabled) chamado quando o switch muda
 * - disabled: boolean (desabilita o switch)
 */
const FixedTimeSwitch = ({ date, dayOfWeek, initialEnabled = false, onChange, disabled = false }) => {
  const [enabled, setEnabled] = useState(initialEnabled);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setEnabled(initialEnabled);
  }, [initialEnabled]);

  const handleToggle = async (event) => {
    const newValue = event.target.checked;
    
    setLoading(true);
    try {
      // Atualiza no backend
      await api.post('/schedule/fixed-time/config', {
        date: date,
        day_of_week: dayOfWeek,
        is_enabled: newValue
      });

      // Atualiza estado local
      setEnabled(newValue);
      
      // Notifica componente pai
      if (onChange) {
        onChange(newValue);
      }
    } catch (error) {
      console.error('Erro ao atualizar configuração de horário fixado:', error);
      // Reverte em caso de erro
      setEnabled(!newValue);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip 
      title={
        enabled 
          ? "Horário fixado ativado: usa start_time do schedule para cálculo de salário" 
          : "Horário fixado desativado: usa horário real de clock_in"
      }
      arrow
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        {loading ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <Clock size={16} color={enabled ? '#65C466' : '#999'} />
            <IOSSwitch
              checked={enabled}
              onChange={handleToggle}
              disabled={disabled || loading}
            />
          </>
        )}
      </Box>
    </Tooltip>
  );
};

export default FixedTimeSwitch;
